/**
 * Where each chart page sits, on a ring.
 *
 * The mobile shell shows one chart group at a time and cycles through them,
 * wrapping — two groups go A → B → A. Every page is a live SciChart surface, so
 * the one thing this must never do is reorder or remount them: a rebuild costs a
 * WebGL teardown plus a data refetch, and "go back to the first chart" would
 * pay it every lap.
 *
 * So the pages are fixed in the DOM and only their **offsets** change. A page at
 * offset 0 is on screen; ±1 are its neighbours, waiting off either edge. Moving
 * on is arithmetic over those numbers, never a change to the list.
 *
 * This lives here rather than in `ChartPager` for the reason `chip-rows.ts` and
 * `chart-window.ts` do: everything that touches a surface is out of jsdom's
 * reach, so the decidable half is separated out and tested on its own.
 *
 * ## Why a page cannot simply keep its offset
 *
 * With three pages and a step forward, the page arriving is the one at +1 and
 * the page leaving goes to −1. Fine. But the *third* page then holds −1 too,
 * and on the next step it would have to travel from −1 to 0 by crossing the
 * whole screen backwards, against the direction of travel.
 *
 * The fix is not a cleverer formula but a **teleport while off-screen**: the
 * incoming page is moved to the side it must enter from with the transition
 * suppressed, and only then animated in. `enterFrom` states which side that is;
 * `ringOffsets` states where everything rests once it arrives.
 */

/**
 * Each page's resting offset, in pages, when `current` is the one on screen.
 *
 * Index `i` of the result is page `i`. `0` means on screen, `-1` waiting off the
 * left edge, `+1` off the right. With more than three pages the extras park at
 * `+2` — off screen and out of the way, since only the neighbours can be reached
 * in one step.
 *
 * With **two** pages the neighbour is both the next and the previous, and it is
 * parked on the right: `nextIndex` and `prevIndex` agree that it is one step
 * either way, so the side it waits on is a choice, and the right is the one that
 * matches the forward direction the tab-tap always travels in.
 */
export function ringOffsets(count: number, current: number): number[] {
  if (count <= 0) return [];
  const at = wrap(current, count);
  return Array.from({ length: count }, (_, i) => {
    if (i === at) return 0;
    if (i === nextIndex(count, at)) return 1;
    if (i === prevIndex(count, at)) return -1;
    return 2;
  });
}

/** The page a forward step lands on. */
export function nextIndex(count: number, current: number): number {
  return count <= 0 ? 0 : wrap(current + 1, count);
}

/** The page a backward step lands on. */
export function prevIndex(count: number, current: number): number {
  return count <= 0 ? 0 : wrap(current - 1, count);
}

/**
 * The offset the incoming page must be placed at *before* it animates.
 *
 * Forward means the new page arrives from the right, so it starts at `+1`.
 * Applied with the transition off — see the header. Placing it correctly is
 * what makes a wrap indistinguishable from an ordinary step.
 */
export function enterFrom(direction: 1 | -1): number {
  return direction === 1 ? 1 : -1;
}

/**
 * Whether stepping is possible at all.
 *
 * One chart group has nothing to cycle to, and a tap on the Chart tab should
 * then do what every other tab does — nothing, because you are already there.
 */
export function canStep(count: number): boolean {
  return count > 1;
}

/** Positive modulo. `-1 % 3` is `-1` in JS, and a page index is never negative. */
function wrap(index: number, count: number): number {
  return ((index % count) + count) % count;
}

/** How far a finger must travel sideways along the symbol row to page. */
export const SWIPE_PX = 40;

/**
 * A swipe along the chart's bottom row, read as a page step: leftwards is the
 * next chart, rightwards the previous, as a page slides under the finger. It
 * must be mostly sideways, because the grip on the same row is a *vertical*
 * drag (the symbol wheel) and the two must never both fire.
 */
export function swipeStep(dx: number, dy: number): 1 | -1 | 0 {
  if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < 1.5 * Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}
